// Phase D — Ration Advances Page.
//
// Shows the weekly ration cash-advance log for all SANCTIONED employees.
// HR can trigger generation for any date (normally auto-done each Saturday).
//
// Tabs:
//   1. Advances log  — filterable by period / status
//   2. Period summary — PAID / WITHHELD / RECOVERED totals

import { useState } from 'react'
import {
  App, Badge, Button, Card, Col, DatePicker, Form, Modal,
  Row, Select, Space, Statistic, Table, Tabs, Tag, Typography,
} from 'antd'
import {
  CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined,
  PlayCircleOutlined, ReloadOutlined, SyncOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'

import {
  welfarePayrollApi,
  type AdvancesSummary,
  type GenerateAdvancesResult,
  type RationAdvanceRow,
} from '@/features/hr/welfarePayrollApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Text } = Typography
const { Option } = Select

const STATUS_COLOR: Record<string, string> = {
  PAID: 'success',
  WITHHELD: 'warning',
  RECOVERED: 'processing',
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export default function RationAdvancesPage() {
  const { plantId } = usePlantId()
  const now = dayjs()

  const [filterYear, setFilterYear] = useState<number>(now.year())
  const [filterMonth, setFilterMonth] = useState<number>(now.month() + 1)
  const [filterStatus, setFilterStatus] = useState<string | undefined>()
  const [generateOpen, setGenerateOpen] = useState(false)

  const { data: advances, isLoading: listLoading, refetch } = useQuery({
    queryKey: ['ration-advances', plantId, filterYear, filterMonth, filterStatus],
    queryFn: () =>
      welfarePayrollApi.listAdvances({
        plant_id: plantId ?? undefined,
        period_year: filterYear,
        period_month: filterMonth,
        status: filterStatus,
        limit: 500,
      }),
    enabled: !!plantId,
  })

  const { data: summary } = useQuery({
    queryKey: ['ration-advances-summary', plantId, filterYear, filterMonth],
    queryFn: () =>
      welfarePayrollApi.summary(filterYear, filterMonth, plantId ?? undefined),
    enabled: !!plantId,
  })

  return (
    <div>
      <PageHeader
        title="Ration Advances"
        subtitle="Weekly cash advances paid to sanctioned ration employees. Generated each Saturday; recovered at month-end payroll."
        extra={
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => setGenerateOpen(true)}
          >
            Generate Advances
          </Button>
        }
      />

      {/* Period Summary Cards */}
      {summary && <SummaryCards summary={summary} />}

      {/* Filters */}
      <Card style={{ marginTop: 16, marginBottom: 8 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap>
          <Text type="secondary">Period:</Text>
          <Select
            value={filterYear}
            onChange={setFilterYear}
            style={{ width: 90 }}
            options={[now.year() - 1, now.year(), now.year() + 1].map((y) => ({
              label: y, value: y,
            }))}
          />
          <Select
            value={filterMonth}
            onChange={setFilterMonth}
            style={{ width: 130 }}
            options={MONTH_NAMES.map((name, i) => ({ label: name, value: i + 1 }))}
          />
          <Select
            allowClear
            placeholder="All statuses"
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: 140 }}
          >
            <Option value="PAID">Paid</Option>
            <Option value="WITHHELD">Withheld</Option>
            <Option value="RECOVERED">Recovered</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Space>
      </Card>

      {/* Advances Table */}
      <Card>
        <Table<RationAdvanceRow>
          rowKey="id"
          size="small"
          loading={listLoading}
          pagination={{ pageSize: 50, showTotal: (t) => `${t} records` }}
          locale={{ emptyText: 'No ration advances for this period.' }}
          dataSource={advances ?? []}
          columns={[
            {
              title: 'Employee',
              key: 'emp',
              width: 220,
              render: (_: unknown, r: RationAdvanceRow) => (
                <Space direction="vertical" size={0}>
                  <Text strong style={{ fontSize: 13 }}>{r.emp_name ?? '—'}</Text>
                  <Text code style={{ fontSize: 10 }}>{r.emp_code ?? r.employee_id.slice(0, 8)}</Text>
                </Space>
              ),
            },
            {
              title: 'Advance Date',
              dataIndex: 'advance_date',
              key: 'date',
              width: 120,
              render: (v: string) => dayjs(v).format('DD MMM YYYY'),
              sorter: (a: RationAdvanceRow, b: RationAdvanceRow) =>
                a.advance_date.localeCompare(b.advance_date),
              defaultSortOrder: 'descend',
            },
            {
              title: 'Amount',
              dataIndex: 'amount',
              key: 'amount',
              width: 110,
              align: 'right' as const,
              render: (v: number) => (
                <Text strong>₹{Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0 })}</Text>
              ),
            },
            {
              title: 'Attendance',
              key: 'att',
              width: 120,
              render: (_: unknown, r: RationAdvanceRow) => {
                if (r.attendance_days_in_week === null) return <Text type="secondary">—</Text>
                return (
                  <Space size={4}>
                    {r.attendance_satisfied
                      ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                    <Text style={{ fontSize: 12 }}>
                      {r.attendance_days_in_week}d / week
                    </Text>
                  </Space>
                )
              },
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 110,
              render: (v: string) => (
                <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag>
              ),
            },
            {
              title: 'Recovered',
              key: 'recovered',
              width: 130,
              render: (_: unknown, r: RationAdvanceRow) => {
                if (!r.recovered_at) return <Text type="secondary">—</Text>
                return (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {dayjs(r.recovered_at).format('DD MMM YYYY')}
                  </Text>
                )
              },
            },
          ]}
        />
      </Card>

      <GenerateModal
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        plantId={plantId}
      />
    </div>
  )
}


// ─── Summary Cards ───────────────────────────────────────────────────────

function SummaryCards({ summary }: { summary: AdvancesSummary }) {
  const byStatus = Object.fromEntries(
    summary.by_status.map((s) => [s.status, s]),
  )
  const paid = byStatus['PAID']
  const withheld = byStatus['WITHHELD']
  const recovered = byStatus['RECOVERED']
  const totalPaid = (paid?.total ?? 0) + (withheld?.total ?? 0)

  return (
    <Row gutter={16} style={{ marginTop: 16 }}>
      <Col xs={24} sm={8}>
        <Card>
          <Statistic
            title="Paid this period"
            value={paid?.total ?? 0}
            prefix="₹"
            valueStyle={{ color: '#52c41a' }}
            suffix={
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({paid?.count ?? 0} advances)
              </Text>
            }
          />
        </Card>
      </Col>
      <Col xs={24} sm={8}>
        <Card>
          <Statistic
            title="Withheld (attendance)"
            value={withheld?.total ?? 0}
            prefix="₹"
            valueStyle={{ color: '#fa8c16' }}
            suffix={
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({withheld?.count ?? 0} advances)
              </Text>
            }
          />
        </Card>
      </Col>
      <Col xs={24} sm={8}>
        <Card>
          <Statistic
            title="Recovered (payroll)"
            value={recovered?.total ?? 0}
            prefix="₹"
            valueStyle={{ color: '#1677ff' }}
            suffix={
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({recovered?.count ?? 0} advances)
              </Text>
            }
          />
        </Card>
      </Col>
    </Row>
  )
}


// ─── Generate Advances Modal ─────────────────────────────────────────────

function GenerateModal({
  open, onClose, plantId,
}: {
  open: boolean
  onClose: () => void
  plantId: string | null
}) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()

  const generateMut = useMutation({
    mutationFn: ({ advance_date }: { advance_date: string }) =>
      welfarePayrollApi.generateAdvances(advance_date, plantId ?? undefined),
    onSuccess: (result: GenerateAdvancesResult) => {
      qc.invalidateQueries({ queryKey: ['ration-advances'] })
      qc.invalidateQueries({ queryKey: ['ration-advances-summary'] })
      message.success(
        `Generated: ${result.generated} | Withheld: ${result.withheld} | Already existed: ${result.skipped_duplicate}`,
        6,
      )
      form.resetFields()
      onClose()
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Generation failed')
    },
  })

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnClose
      title={
        <Space>
          <PlayCircleOutlined style={{ color: '#52c41a' }} />
          Generate Ration Advances
        </Space>
      }
      okText="Generate"
      confirmLoading={generateMut.isPending}
      onOk={() =>
        form.validateFields().then((values: { advance_date: Dayjs }) => {
          generateMut.mutate({
            advance_date: values.advance_date.format('YYYY-MM-DD'),
          })
        })
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="advance_date"
          label="Advance Date"
          rules={[{ required: true, message: 'Select a date' }]}
          initialValue={dayjs()}
          help="Typically a Saturday. Advances are computed for the 7-day window ending on this date."
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD MMM YYYY"
            disabledDate={(d) => d && d > dayjs().endOf('day')}
          />
        </Form.Item>
      </Form>

      <Card
        size="small"
        style={{ background: '#f6ffed', border: '1px solid #b7eb8f', marginTop: 8 }}
      >
        <Space direction="vertical" size={4}>
          <Text style={{ fontSize: 12 }}>
            <SyncOutlined style={{ color: '#52c41a', marginRight: 4 }} />
            Only processes employees with ration_sanction_status = SANCTIONED.
          </Text>
          <Text style={{ fontSize: 12 }}>
            <CalendarOutlined style={{ color: '#1677ff', marginRight: 4 }} />
            Idempotent — already-generated advances for this date are skipped.
          </Text>
          <Text style={{ fontSize: 12 }}>
            <Badge color="#fa8c16" />
            Advances are auto-recovered at month-end payroll run via RATION_ADVANCE_RECOVERY deduction.
          </Text>
        </Space>
      </Card>
    </Modal>
  )
}
